import React from "react";
import {
  Box,
  Button,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CREDIT_STATUS_COLOR,
  pill,
  formatDate,
  formatDateTime,
  tableHeadCellSx,
  tableBodyCellSx,
} from "bundles/Internal/components/CompanyShowPage";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

function CompanyCreditsTab({ credits, loading, onEditCredit, onDeleteCredit }) {
  if (loading) {
    return (
      <Stack spacing={1} sx={{ p: 4 }}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (credits.length === 0) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
          No credits for this company.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Id", "Type", "Status", "Expires at", "Job offer", "Recruiter", "Created by", "Created", "Updated", ""].map((label) => (
              <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {credits.map((c) => {
            const statusColor = CREDIT_STATUS_COLOR[c.status] || CREDIT_STATUS_COLOR.pending;
            return (
              <TableRow key={c.id} hover>
                <TableCell sx={tableBodyCellSx}>{c.id}</TableCell>
                <TableCell sx={tableBodyCellSx}>{c.credit_type || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {c.status ? pill(c.status, statusColor) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDate(c.expire_at) || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{c.job_offer_id ?? "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{c.recruiter_id ?? "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {c.created_by ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <UserAvatar user={c.created_by} size={24} />
                      <span>{c.created_by.full_name}</span>
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(c.created_at) || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(c.updated_at) || "—"}</TableCell>
                <TableCell sx={{ ...tableBodyCellSx, width: 150, px: 1 }}>
                  <Button
                    size="small"
                    disableRipple
                    onClick={() => onEditCredit?.(c)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "hsl(150 38% 17%)",
                      border: "1px solid hsl(140 8% 88%)",
                      borderRadius: "6px",
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
                    }}
                  >
                    Modify
                  </Button>
                  <Button
                    size="small"
                    disableRipple
                    onClick={() => onDeleteCredit?.(c)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      ml: 0.5,
                      fontSize: 11,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "hsl(0 72% 51%)",
                      border: "1px solid hsl(0 72% 85%)",
                      borderRadius: "6px",
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "hsl(0 72% 97%)" },
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default CompanyCreditsTab;
